/**
 * AVBP Intake Stage Engine — the config-driven ordered-stage spine that serves
 * ALL FOUR multi-report patterns (one-intake-two-reports, n-properties,
 * tier-carry-forward, phase-gated) via a single mechanic.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS (spine config `unifying_mechanic`):
 *    "a single stage engine (ordered module-stages, per-stage send/reminder,
 *     previous stages read-only) implements all four via config."
 *
 *  This module owns the PURE engine logic — stage ordering, gating, status
 *  computation, and the merge-safe doc field shapes. It is deliberately
 *  Firebase-free above the impure footer so the Node `.cjs` test runner can
 *  exercise ordering / gating / merge / lock without a browser.
 *
 *  SCOPE OF THIS DISPATCH (KEYSTONE = the stage engine, built FIRST):
 *    • Ordered, config-driven stages (per-tier module-group ordering).
 *    • Consent as the mandatory, always-on Stage 0 (cannot advance without it).
 *    • Resumable + partial-save per stage; previous stages read-only once
 *      submitted (an explicit "edit" affordance unlocks one).
 *    • A per-stage completion/status map persisted ALONGSIDE `answers` — the
 *      `answers` shape itself is UNCHANGED (the locked composer contract).
 *
 *  NOT IN SCOPE (deferred — see AVBP_INTAKE_STAGE_ENGINE_STATE.md):
 *    • The person/family graph (NEXT dispatch).
 *    • The full 46-module registry build-out / per-tier NEW intakes.
 *    • Multi-property client UI; the reminder scheduler (seam left, below).
 *
 *  THE DATA CONTRACT IS PRESERVED. Each module-stage still reads/writes the
 *  SAME `answers.*` keys via the existing `intake.ts` builders. The stage
 *  engine adds exactly two NEW sibling fields on the intake doc, both
 *  merge-safe (they never touch `answers`):
 *    • `consent`   — the captured acceptance (timestamp, who, version, flags).
 *    • `stages`    — { [stageId]: { status, submitted_at, submitted_by } }.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { REQUIRED_CONSENT_KEYS } from './consentContent';
import { activeIntakeCatalog, type IntakeStageSpec } from './intakeCatalog';
import type { ModuleFlag } from './intakeModules';

/* ───────────────────────────── stage status ──────────────────────────── */

/**
 * The lifecycle of one stage:
 *   - 'locked'      : a future stage not yet reachable (a prior stage is open).
 *   - 'in_progress' : the current editable stage.
 *   - 'submitted'   : completed + read-only (re-openable via explicit edit).
 */
export const STAGE_STATUSES = ['locked', 'in_progress', 'submitted'] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

/** Per-stage status as persisted on the intake doc under `stages.{id}`. */
export interface StageRecord {
  status: StageStatus;
  /** ISO-ish or server stamp recorded at the write site (string when local). */
  submitted_at?: string | null;
  submitted_by?: string | null;
}

/** The whole persisted `stages` map: stageId → record. */
export type StagesMap = Record<string, StageRecord>;

/* ───────────────────────────── stage config ──────────────────────────── */

/**
 * A stage is an ORDERED group of one or more module keys (from the spine
 * `module_registry`). The engine never hardcodes ONE tier — a tier's stage list
 * is resolved LIVE from the service catalog adapter (`intakeCatalog.ts`), so a
 * service's module-selection (and its NAME) drives the rendered form with no
 * code change. Build the module library once; a service is just a SELECTION.
 */
export interface StageDef {
  /** Stable id persisted in the `stages` map (== IntakeStageSpec.stageKey). */
  id: string;
  /** Client-facing title (progress indicator + section header). */
  title: string;
  /** One-line helper under the title. */
  blurb?: string;
  /**
   * The module-registry keys this stage renders, in order. The renderer
   * (IntakeModule.tsx) renders each via the module library. Consent's special
   * stage carries `consent_permissions`; others carry the selected field modules.
   */
  moduleKeys: string[];
  /** Per-module flag (moduleKey → 'mandatory'|'on'|'off') for this stage. */
  fieldFlags?: Record<string, ModuleFlag>;
  /**
   * Consent is the always-on Stage 0 with bespoke gating (all REQUIRED tick
   * boxes). Marked so the engine + UI treat it specially.
   */
  consent?: boolean;
}

/**
 * The CONSENT / "Review & submit" stage — always-on, always LAST, identical
 * across every tier. Owner ruling 2026-06-22: consent moved from the FRONT to
 * the END — the final tab is where the client reviews everything, ticks consent
 * & privacy, and submits (Save & Submit is gated on the required boxes; the
 * Review & Print Summary button also lives here). Held as a constant so it can
 * never be reordered out of the last position or dropped from a tier.
 */
export const CONSENT_STAGE: StageDef = {
  id: 'consent',
  title: 'Review & submit',
  blurb: 'Review everything, agree to consent & privacy, then submit.',
  moduleKeys: ['consent_permissions'],
  consent: true,
};

/** The tier the current intake builds against (the existing live L2B intake). */
export const DEFAULT_STAGE_TIER = 'L2B_R1' as const;

/**
 * Per-tier stage-spec OVERRIDES, for tests / hand-authored flows. Normally
 * EMPTY — the live source is the catalog adapter (`activeIntakeCatalog`). An
 * entry here wins over the catalog for that tier (used by the engine test to
 * inject a malformed/duplicate-consent tier and prove the defensive ordering).
 * Production never populates this; it is purely an injection seam.
 */
export const TIER_STAGES: Record<string, IntakeStageSpec[]> = {};

/* ─────────────────────────── pure engine logic ───────────────────────── */

/** Map an IntakeStageSpec (catalog contract) → a StageDef (engine shape). */
function specToStage(spec: IntakeStageSpec): StageDef {
  return {
    id: spec.stageKey,
    title: spec.title,
    blurb: spec.blurb,
    moduleKeys: spec.moduleKeys,
    fieldFlags: spec.fieldFlags,
    consent: spec.moduleKeys.length === 1 && spec.moduleKeys[0] === 'consent_permissions',
  };
}

/**
 * PURE — resolve the ordered stage list for a tier, LIVE from the service
 * catalog adapter (name + module-selection read from the catalog, never
 * hardcoded). Defensive: guarantees the CONSENT / "Review & submit" stage is
 * present AND LAST (always-on, always last — owner ruling 2026-06-22), even if
 * the catalog forgets it or misorders it. A tier with no catalog stages still
 * yields a valid flow (the Review & submit stage only).
 *
 * A `TIER_STAGES` override (test injection) wins over the catalog for that tier.
 */
export function resolveStages(tier: string = DEFAULT_STAGE_TIER): StageDef[] {
  const specs: IntakeStageSpec[] =
    TIER_STAGES[tier] && TIER_STAGES[tier].length
      ? TIER_STAGES[tier]
      : activeIntakeCatalog.intakeStages(tier);
  const raw = specs.map(specToStage);
  // Strip any consent entries the source may carry, then APPEND the canonical
  // CONSENT_STAGE so the last position is invariant + never duplicated.
  const rest = raw.filter((s) => !s.consent && s.id !== CONSENT_STAGE.id);
  return [...rest, CONSENT_STAGE];
}

/**
 * PURE — the stage list for a `birth_only` top-up.
 *
 * Flag #1 (owner-approved 2026-06-20): birth_only WAIVES the always-on consent
 * stage. A DOB top-up is requested for an EXISTING client who already gave
 * consent at primary intake, so re-gating is pure friction with no compliance
 * gain. The flow therefore opens STRAIGHT to the single "Birth details" stage
 * (no CONSENT_STAGE prepended). Consent is carried forward implicitly via a
 * `consent_source:'primary_intake'` marker stamped on the sidecar doc at the
 * save site (saveBirthOnlyConsentCarry) — NOT via a rendered stage.
 *
 * This is deliberately separate from resolveStages so the report tiers
 * (L1A/L1B/L2B/L3A/L3B) — which DO keep the full consent gate — are never
 * affected by the waiver.
 */
export function birthOnlyStages(): StageDef[] {
  return [
    {
      id: 'person',
      title: 'Birth details',
      blurb: 'Date, time and place of birth for the astrology reading.',
      moduleKeys: ['person_birth_data'],
      fieldFlags: { person_birth_data: 'mandatory' },
    },
  ];
}

/**
 * PURE — does the captured acceptance satisfy the REQUIRED consent gate?
 * Every required tick-box key must be present AND true. The optional
 * astro/health box is ignored by the gate.
 */
export function consentAccepted(
  acceptance: ConsentAcceptance | null | undefined,
): boolean {
  if (!acceptance || !acceptance.accepted) return false;
  const flags = acceptance.flags || {};
  return REQUIRED_CONSENT_KEYS.every((k) => flags[k] === true);
}

/**
 * PURE — is a given stage's persisted record "complete enough" to advance past
 * it? For the consent stage, completion === consentAccepted (status alone is
 * not trusted — the acceptance flags are the source of truth). For any other
 * stage, completion === status 'submitted'.
 *
 * CONSENT IS THE CLIENT'S, NOT STAFF'S (owner ruling 2026-06-16). When a STAFF
 * member fills on the client's behalf (`viewerIsStaff`), consent is NEVER a gate
 * for them — the consent stage is treated as already "satisfied" so staff
 * proceed straight into the form. Staff are shown the client's consent STATUS
 * instead of a sign-box; they never accept on the client's behalf. The client
 * self-fill path is unchanged: consent stays the always-on Stage 0 gate.
 */
export function isStageComplete(
  stage: StageDef,
  rec: StageRecord | undefined,
  acceptance: ConsentAcceptance | null | undefined,
  viewerIsStaff = false,
): boolean {
  if (stage.consent) return viewerIsStaff ? true : consentAccepted(acceptance);
  return rec?.status === 'submitted';
}

/**
 * PURE — given the ordered stages + the persisted `stages` map + the consent
 * acceptance, compute the index of the FIRST stage that is not yet complete
 * (the "current" stage to resume into). Returns stages.length when ALL are
 * complete (the flow is finished). This is the resumability core.
 */
export function firstIncompleteIndex(
  stages: StageDef[],
  stagesMap: StagesMap | null | undefined,
  acceptance: ConsentAcceptance | null | undefined,
  viewerIsStaff = false,
): number {
  const map = stagesMap || {};
  for (let i = 0; i < stages.length; i++) {
    if (!isStageComplete(stages[i], map[stages[i].id], acceptance, viewerIsStaff)) return i;
  }
  return stages.length;
}

/**
 * PURE — can the user navigate (forward) to `targetIndex`? Forward navigation
 * is only allowed up to the first incomplete stage: you cannot skip ahead past
 * an unfinished stage. Backward navigation to any already-submitted stage is
 * always allowed (read-only view / explicit edit) and handled by the UI, so
 * this gate is about the FORWARD reach only.
 */
export function canReachStage(
  targetIndex: number,
  stages: StageDef[],
  stagesMap: StagesMap | null | undefined,
  acceptance: ConsentAcceptance | null | undefined,
  viewerIsStaff = false,
): boolean {
  if (targetIndex < 0 || targetIndex >= stages.length) return false;
  const firstOpen = firstIncompleteIndex(stages, stagesMap, acceptance, viewerIsStaff);
  return targetIndex <= firstOpen;
}

/**
 * PURE — is a stage READ-ONLY for the given map? A stage is read-only iff its
 * record is 'submitted' (and, for consent, the acceptance is satisfied). The
 * UI offers an explicit "Edit" affordance that flips it back to 'in_progress'.
 */
export function isStageReadOnly(
  stage: StageDef,
  rec: StageRecord | undefined,
  acceptance: ConsentAcceptance | null | undefined,
  viewerIsStaff = false,
): boolean {
  // For staff, the consent stage is a STATUS panel, not an editable form —
  // staff never accept on the client's behalf, so it is always "read-only" to
  // them (no sign-box rendered). Field stages follow normal completion.
  if (stage.consent && viewerIsStaff) return true;
  return isStageComplete(stage, rec, acceptance, viewerIsStaff);
}

/**
 * PURE — render-ready progress descriptor: "Stage N of M" plus a per-stage
 * state array suitable for the locked `StageRail` component
 * ({label, state:'done'|'active'|undefined}).
 */
/** Rail state vocabulary matches the locked StageRail component (ui.tsx). */
export type RailState = 'done' | 'now' | 'todo';

export interface StageProgress {
  current: number; // 0-based index of the active stage
  total: number;
  rail: { id: string; label: string; state: RailState }[];
}

export function stageProgress(
  stages: StageDef[],
  activeIndex: number,
  stagesMap: StagesMap | null | undefined,
  acceptance: ConsentAcceptance | null | undefined,
  viewerIsStaff = false,
): StageProgress {
  const map = stagesMap || {};
  return {
    current: activeIndex,
    total: stages.length,
    rail: stages.map((s, i) => {
      const done = isStageComplete(s, map[s.id], acceptance, viewerIsStaff);
      const state: RailState = i === activeIndex ? 'now' : done ? 'done' : 'todo';
      return { id: s.id, label: s.title, state };
    }),
  };
}

/**
 * PURE — a plain-language summary of the CLIENT's consent status, for the STAFF
 * status panel (staff never sign — they see where the client's consent stands).
 * Returns whether consent is on the doc + a human one-liner. `acceptedAtText`
 * is whatever the doc stored (a server Timestamp renders as an object client-
 * side, so the caller may pass a pre-formatted date; this helper handles a
 * string/null and falls back to "on file").
 */
export interface ConsentStatusView {
  obtained: boolean;
  /** A one-line human summary for the staff panel. */
  summary: string;
}

export function consentStatusFor(
  acceptance: ConsentAcceptance | null | undefined,
  acceptedAtText?: string | null,
): ConsentStatusView {
  if (!consentAccepted(acceptance)) {
    return { obtained: false, summary: 'Client consent: not yet collected' };
  }
  const when = acceptedAtText ? ` on ${acceptedAtText}` : '';
  const ver = acceptance?.version ? ` · ${acceptance.version}` : '';
  const via = acceptance?.accepted_via === 'client' ? ' (by the client)' : '';
  return {
    obtained: true,
    summary: `Client consent: obtained${when}${ver}${via}`,
  };
}

/* ───────────────────────── consent acceptance shape ───────────────────── */

/**
 * The acceptance captured at Stage 0 and persisted on the intake doc under the
 * sibling `consent` field (NEVER inside `answers`). Carries WHEN, WHO, the copy
 * VERSION, and the per-box flags. `accepted` is the convenience boolean (all
 * required boxes true) but the gate re-derives it from `flags` defensively.
 */
export interface ConsentAcceptance {
  accepted: boolean;
  version: string;
  /** Per-tick-box state, keyed by ConsentCheckItem.key. */
  flags: Record<string, boolean>;
  /** Server stamp at the write site (string here for the pure layer/tests). */
  accepted_at?: string | null;
  accepted_by?: string | null;
  accepted_via?: 'client' | 'staff';
  /**
   * Staff-recorded provenance: the client gave consent OFFLINE (e.g. on a call)
   * and a staff member logged it. Distinguishes an audited offline record from
   * the client ticking the boxes themselves in-portal. Never set on the client
   * self-fill path.
   */
  obtained_offline?: boolean;
}

/**
 * PURE — build the acceptance record from the tick-box state. `accepted` is
 * true only when every REQUIRED box is true; the optional box rides along in
 * `flags` but does not affect `accepted`.
 */
export function buildConsentAcceptance(
  flags: Record<string, boolean>,
  version: string,
  via: 'client' | 'staff',
): ConsentAcceptance {
  const clean: Record<string, boolean> = {};
  for (const k of Object.keys(flags || {})) clean[k] = flags[k] === true;
  const accepted = REQUIRED_CONSENT_KEYS.every((k) => clean[k] === true);
  return { accepted, version, flags: clean, accepted_via: via };
}

/* ───────────────────────────── impure (Firestore) ─────────────────────── */
/* Everything below imports `db`; the Node test runner never loads it (tests
 * import only the pure exports via _tsload, which short-circuits before
 * touching these). Keep all Firebase access at the bottom. */

import { serverTimestamp } from 'firebase/firestore';
import { L2B_R1_TIER, INTAKE_SCHEMA_VERSION, asScope } from './intake';
import type { IntakeScope } from './intakeScope';
import { loadIntakeDoc, writeIntakeDoc } from './intakeScopeDb';

/**
 * Load the stage-engine sidecar (`consent` + `stages`) from the intake doc.
 * Reads ONLY these two sibling fields — `answers` is loaded separately by
 * `loadIntake`, so the two layers never interfere.
 */
/**
 * Scaffold modules persist their minimal capture here — a sibling field on the
 * intake doc, keyed by moduleKey → { fieldName: value }. NEVER touches
 * `answers` (the locked composer contract). Promote a scaffold → real by
 * moving its fields into `answers` via an intake.ts builder; the extras slot is
 * a holding area, not an engine input.
 */
export type IntakeExtras = Record<string, Record<string, string | boolean>>;

export async function loadStageState(
  scopeOrClientId: IntakeScope | string,
  tier: string = L2B_R1_TIER,
): Promise<{ consent: ConsentAcceptance | null; stages: StagesMap; extras: IntakeExtras }> {
  const hit = await loadIntakeDoc(asScope(scopeOrClientId), tier);
  if (!hit) return { consent: null, stages: {}, extras: {} };
  const data = hit.data;
  const consent = (data.consent as ConsentAcceptance) ?? null;
  const stages = (data.stages as StagesMap) ?? {};
  const extras = (data.intake_extras as IntakeExtras) ?? {};
  return { consent, stages, extras };
}

/**
 * Persist ONE scaffold module's captured values under `intake_extras.{moduleKey}`
 * via a DOTTED field path so only that module's slot is replaced — siblings (and
 * `answers`, `consent`, `stages`) are preserved (merge-safe). Mirrors the
 * one-owner discipline of `saveStageStatus`.
 */
export async function saveModuleExtras(
  scopeOrClientId: IntakeScope | string,
  moduleKey: string,
  values: Record<string, string | boolean>,
  actor: { uid?: string | null; via: 'client' | 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    [`intake_extras.${moduleKey}`]: values,
    schema_version: INTAKE_SCHEMA_VERSION,
    tier,
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/**
 * Persist the captured consent acceptance — MERGE-WRITE under the sibling
 * `consent` field (DOTTED so the map replaces wholesale, never leaving a stale
 * flag) plus a server `accepted_at`/`accepted_by`. `answers` is untouched, so a
 * staff hybrid-fill of the technical fields is never clobbered (locked merge
 * constraint). Also stamps the schema_version/tier so a consent-first save on a
 * brand-new doc carries the envelope identity.
 */
export async function saveConsent(
  scopeOrClientId: IntakeScope | string,
  acceptance: ConsentAcceptance,
  actor: { uid?: string | null; via: 'client' | 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    schema_version: INTAKE_SCHEMA_VERSION,
    tier,
    // dotted field path → the consent map replaces wholesale (no orphan flag)
    consent: {
      ...acceptance,
      accepted_at: serverTimestamp(),
      accepted_by: actor.uid || null,
      accepted_via: actor.via,
    },
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/**
 * Persist ONE stage's status record under `stages.{stageId}` via a DOTTED field
 * path so only that one stage's record is replaced — sibling stage records (and
 * `answers`, and `consent`) are preserved (merge-safe, per the locked
 * constraint that client + staff writes never clobber). This is what "partial
 * save per stage" writes after the stage's own `saveIntake` has merged the
 * `answers` fields.
 */
export async function saveStageStatus(
  scopeOrClientId: IntakeScope | string,
  stageId: string,
  status: StageStatus,
  actor: { uid?: string | null; via: 'client' | 'staff' },
  tier: string = L2B_R1_TIER,
): Promise<void> {
  await writeIntakeDoc(asScope(scopeOrClientId), tier, {
    [`stages.${stageId}`]: {
      status,
      submitted_at: status === 'submitted' ? serverTimestamp() : null,
      submitted_by: status === 'submitted' ? actor.uid || null : null,
    },
    updated_at: serverTimestamp(),
    updated_by: actor.uid || null,
    updated_via: actor.via,
  });
}

/* ─────────────────────────── per-stage send/reminder seam ──────────────── */
/**
 * SEAM (deferred — reminder scheduler is OUT OF SCOPE this dispatch).
 *
 * The unifying mechanic calls for "per-stage send/reminder". The engine is
 * structured so a single stage can be individually addressed: every stage has
 * a stable `id`, and its status lives at `stages.{id}` on the intake doc. A
 * future reminder scheduler (a Cloud Function on a schedule, or a "Send this
 * stage" button) needs only:
 *   1. resolveStages(tier) — to know the stage list + ids,
 *   2. loadStageState()    — to find which stages are not yet 'submitted',
 *   3. a link of the form  /intake/:token?stage={id}  (the route already reads
 *      a `stage` param via the UI deep-link, below),
 *   4. the client's channel (Brevo email / AiSensy WA) from the contact spine.
 * No scheduler, no channel send, and no reminder state are built here — only
 * the addressable seam. Flagged for Varun in the state file.
 */
export const STAGE_REMINDER_SEAM = {
  deep_link: (token: string, stageId: string) =>
    `/intake/${encodeURIComponent(token)}?stage=${encodeURIComponent(stageId)}`,
  note: 'reminder scheduler deferred — see AVBP_INTAKE_STAGE_ENGINE_STATE.md',
} as const;
